import { useEffect, useRef, useState } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppSelector } from '@/store/hooks'
import { cn } from '@/lib/utils'
import { useBlockEditQuote } from '@/features/ai/useBlockEditQuote'
import { useCourseQuote } from '@/features/ai/useCourseQuote'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { useIsBetaAuthor } from '@/features/moderation/lib/useIsBetaAuthor'
import { AIComposer } from './aiSidebar/AIComposer'
import { AiUnavailableNotice } from './aiSidebar/AiUnavailableNotice'
import { useAutoOpenForJob } from './aiSidebar/useAutoOpenForJob'
import { ComposerAlerts } from './aiSidebar/ComposerAlerts'
import { resolveComposerGates } from './aiSidebar/composerGates'
import { resolvePlaceholder } from './aiSidebar/placeholders'
import { SidebarBody } from './aiSidebar/SidebarBody'
import { useAiChat } from './aiSidebar/useAiChat'
import { useEditScope } from './aiSidebar/useEditScope'
import { useGenerationSettings } from './aiSidebar/useGenerationSettings'
import { useSidebarBusyFlow } from './aiSidebar/useSidebarBusyFlow'
import { useHasUnsavedBlockInScope } from './aiSidebar/useUnsavedBlockGate'
import type { SidebarMode } from './aiSidebar/types'
import { SIDEBAR_OPEN_W_CLASS, SIDEBAR_W_CLASS } from './layout'

export const AISidebar = ({ course }: { course: CourseWithoutBlocks }) => {
  const isOpen = useAppSelector((s) => s.editor.panels.aiSidebarOpen)
  const [mode, setMode] = useState<SidebarMode>('idle')
  // Значение поля живёт здесь, а не в композере: черновик промпта не должен
  // теряться при переключении режима.
  const [prompt, setPrompt] = useState('')
  const settings = useGenerationSettings()
  const quote = useCourseQuote(course.id, settings.lessonCount, !isOpen)
  const chat = useAiChat(course.id)
  const busy = useSidebarBusyFlow(course.id, isOpen, setMode, chat)
  // Единственный источник области правки (I-1 для E): и submit(), и экран
  // настроек в SidebarBody читают ОДИН и тот же хук.
  const editScope = useEditScope()
  const editQuote = useBlockEditQuote(course.id, editScope.count, !isOpen)
  const hasUnsavedBlockInScope = useHasUnsavedBlockInScope(editScope.blockIds)
  // Признаки гашения ОДНИ на гейты и плейсхолдер: у каждого погашенного поля
  // своя подпись причины. Проверка курса гасит оба платных старта панели —
  // они пишут в контент заявки.
  const isFrozen = useIsCourseFrozen(course.id)
  const offFlags = { isRunning: chat.isRunning, isBusy: busy.isBusy, isFrozen }
  // Панель разворачивается сама, как только у курса появляется запись в реестре (C-1).
  useAutoOpenForJob(course.id)

  const asideRef = useRef<HTMLElement>(null)

  // Minor 2 (ревью раунд 2): гард «Повторить» обязан совпадать со ВСЕМИ
  // гардами обработчика (`canSubmit`), иначе кнопка мертва ровно на том
  // промпте, ради которого автор её и нажал. Формулы гейтов — в
  // `composerGates.ts` (не здесь): держит сложность компонента под лимитом
  // и не даёт риску №3 брифа («гейты не должны разъехаться») размазаться.
  const { isInputDisabled, canSubmit, activeFailure, promptMax } = resolveComposerGates({
    mode,
    ...offFlags,
    isLost: chat.isLost,
    editScopeCount: editScope.count,
    hasUnsavedBlockInScope,
    failure: chat.failure,
    editFailure: chat.editFailure,
    trimmedPromptLength: prompt.trim().length,
  })

  useEffect(() => {
    asideRef.current?.toggleAttribute('inert', !isOpen)
  }, [isOpen])

  const submit = async () => {
    // Гард ОДИН и тот же, что гасит кнопку и «Повторить»: разъехавшиеся гарды
    // это ровно тот дефект, что чинили I-1.
    if (!canSubmit) return
    // Поле чистим ТОЛЬКО по подтверждённому старту: на отказе (503
    // «функция не настроена» — штатный ответ сегодняшнего стенда) текст
    // автора не восстановить ниоткуда, `entry.params` создаётся лишь при
    // успешном старте.
    const started =
      mode === 'edit-block'
        ? await chat.sendBlockEdit(editScope.blockIds, prompt)
        : await chat.send(prompt, settings.lessonCount)
    if (!started) return
    setPrompt('')
    setMode('idle')
  }

  const toggleMode = (next: Exclude<SidebarMode, 'idle'>) => {
    // Вход в ЛЮБОЙ рабочий режим на фоне ещё не закрытого итога закрывает
    // итог: иначе тело панели остаётся на RunOutcome (SidebarBody держит его
    // выше любых настроек), поле композера при этом ЖИВОЕ, и второй платный
    // прогон уходит вслепую — без сметы и показанной области (Important 2,
    // ревью Task 3: раньше это чинилось только для входа в `generate`, на
    // `edit-block` ту же дыру не распространили). Заодно исполняет контракт
    // useCourseJob: detach сразу после показа итога.
    if (chat.outcome) chat.dismissOutcome()
    // Симметрично для плашки пропавшего прогона: она тоже стоит выше настроек
    // ОБОИХ режимов, и без этого живое поле под ней запускало бы прогон,
    // не увидев ни цены, ни объяснения пропажи.
    if (chat.isLost) chat.dismissLost()
    setMode((prev) => (prev === next ? 'idle' : next))
  }

  // Бета-автор — AI пока закрыт заглушкой (временно, по решению заказчика).
  const isBetaAuthor = useIsBetaAuthor(course.id)

  return (
    <aside
      ref={asideRef}
      data-testid="ai-sidebar"
      aria-hidden={!isOpen}
      className={cn(
        'flex h-full flex-col overflow-hidden bg-surface backdrop-blur-sm transition-all duration-300 ease-out',
        isOpen ? SIDEBAR_OPEN_W_CLASS : 'w-0',
      )}
    >
      {/* Свёрнутая панель не рисует содержимое: стейт живёт в хуках выше и переживает сворачивание, дерево сообщений незачем перерисовывать на каждый тик генерации в невидимой панели нулевой ширины. */}
      <div className={cn('relative flex h-full flex-col', SIDEBAR_W_CLASS)}>
        {isOpen && isBetaAuthor ? (
          <>
            <div className="h-14 shrink-0" />
            <AiUnavailableNotice />
          </>
        ) : isOpen ? (
          <>
            {/* Хедера у панели нет — спейсер выравнивает её с EditorHeader (h-14). */}
            <div className="h-14 shrink-0" />

            <SidebarBody
              mode={mode}
              chat={chat}
              busy={busy}
              settings={settings}
              quote={quote}
              editScope={editScope}
              editQuote={editQuote}
              hasUnsavedBlockInScope={hasUnsavedBlockInScope}
            />

            <ComposerAlerts
              mode={mode}
              course={course}
              editScope={editScope}
              failure={activeFailure}
              onRetry={canSubmit ? submit : undefined}
              onShowRun={busy.handleShowRun}
            />

            <AIComposer
              value={prompt}
              onChange={setPrompt}
              mode={mode}
              onToggleMode={toggleMode}
              placeholder={resolvePlaceholder({
                mode,
                // Important 1 (ревью Task 3): область правки — не только
                // выделенный блок, но и «весь урок»; на живом поле в этой
                // области плейсхолдер звал «выберите блок», хотя отправлять
                // уже есть что. Источник — та же область, которой кормится
                // сам старт (`editScope.blockIds`).
                editScope,
                ...offFlags,
                jobType: chat.jobType,
              })}
              isDisabled={isInputDisabled}
              isRunning={chat.isRunning}
              // I-1: единственный источник — тот же, что кормит «Повторить».
              canSubmit={canSubmit}
              promptMax={promptMax}
              onSubmit={submit}
            />
          </>
        ) : null}
      </div>
    </aside>
  )
}
